import React, { useEffect, useState } from 'react';
import FormHeader from '@/components/ui/organisms/MHeader';
import MAvatar from '@/components/ui/atoms/MAvatar';
import { IconUserStar } from '@tabler/icons-react';
import MLoader from '@/components/ui/atoms/MLoader';
import { useFamily } from '@/hooks/useFamily';
import { getMemberInfo } from '@/hooks/useMembers';
import { useMembers } from '@/hooks/useMembers';
import { IconUserX } from '@tabler/icons-react';
import { relationshipOptions } from '@/types/relationship';
import { useNavigate } from 'react-router-dom';
import MemberCard from '../MemberCard';
import { IconChevronRight } from '@tabler/icons-react';
import memberPermissionService from '@/services/member-permissions.service';
import MSpacer from '@/components/ui/atoms/MSpacer';
import MEmptyCard from '@/components/ui/organisms/MEmptyCard';

function getRelationshipLabel(relationship: string | number | undefined) {
  if (typeof relationship === 'number') {
    const rel = relationshipOptions.find(opt => opt.value === relationship);
    return rel ? rel.label : '';
  } else if (typeof relationship === 'string') {
    return relationship;
  }
  return '';
}

interface Member {
  id: string;
  first_name: string;
  last_name?: string;
  relationship?: string;
  family_member_type?: string;
  registered_member?: boolean;
  registration_status?: string;
  nickname?: string;
}

interface ViewerCount {
  [memberId: string]: number;
}

const ManageAccess: React.FC = () => {
  const navigate = useNavigate();
  const { data: familyMembers = [], familyLoading: isLoading } = useFamily();
  const [viewerCounts, setViewerCounts] = useState<Record<string, string[]>>({});
  const memberInfo = getMemberInfo(true);

  // FM logic: only show self if FM
  const isFM = memberInfo?.family_member_type === 'fm';
  const fmMember = isFM ? familyMembers.find(m => m.id === memberInfo.id) : null;
  // Always put owner at the top
  const showMembers = isFM ? (fmMember ? [fmMember] : []) : [...familyMembers].sort((a, b) => {
    if (a.family_member_type === 'fo') return -1;
    if (b.family_member_type === 'fo') return 1;
    return 0;
  });

  // Fetch and prepare access maps when familyMembers change
  useEffect(() => {
    async function fetchAccessCounts() {
      const accessMap = await memberPermissionService.getManagedMemberIds();
      setViewerCounts(accessMap);
    }
    if (familyMembers.length > 0) fetchAccessCounts();
  }, [familyMembers]);

  if (isLoading || (familyMembers.length > 0 && Object.keys(viewerCounts).length === 0)) {
    return <MLoader />;
  }

  return (
    <div className="profile-page-root">
      <FormHeader
        module="Family"
        memberInfo={memberInfo}
        title={isFM ? 'Your profile access' : 'Manage family access'}
        description={isFM ? 'Set who in your family has access to your health records.' : 'Set who in your family has access to each member’s health records.'}
        learnMoreContent={isFM
          ? 'You can decide which family members can see your health information. Only registered family members can be given access.  Granting access allows them to view or update your health records.'
          : <>You can decide which family members can see another member’s health information. Only registered family members can be given access. 
          <br/> <br/> Granting access allows them to view or update health records, but ownership always stays with you as the Family Owner.</>}
        drawerTitle='Learn More'
        hideBack={true}
      />
      <MSpacer mt={24} />
      {!isLoading && familyMembers.length === 1 ? (
        <MEmptyCard
            primaryTitle="No family members yet"
            icon={<IconUserX />}
            secondaryTitle={
          <>
          Add family members to provide access.
          <br/><br/>
          They can then register to view & update health records for other family members.
        </>
        }
          />
      ) :  (
        <div className="flex flex-col gap-4">
          {showMembers.map((member: Member) => {
            const isOwner = member.family_member_type === 'fo';
            const viewers = Array.isArray(viewerCounts?.[member.id]) ? viewerCounts[member.id].length : 0;
            const handleCardClick = () => {
              navigate('/profile/family/manage-access/access-request', {
                state: { selectedMember: member }
              });
            };
            return (
              <div key={member.id} style={{ position: 'relative' }}>
                <MemberCard
                  avatar={<MAvatar
                    size="md"
                    src={member.avatar}
                    type={member.avatar ? 'dp' : 'no-dp'}
                    text={`${member.first_name || ''}${member.last_name ? ' ' + member.last_name : ''}`.trim()}
                    showBadge={isOwner}
                    badgeIcon={isOwner ? <IconUserStar size={16} /> : undefined}
                  />}
                  name={`${member.nickname || member.first_name}`.trim()}
                  isOwner={isOwner}
                  relationship={getRelationshipLabel(member.relationship)}
                  member_type={member.family_member_type}
                  onClick={handleCardClick}
                  className="cursor-pointer"
                  menu={<div style={{display:'flex',alignItems:'center',height:'100%'}}><IconChevronRight size={22} color="#2A2A2A" /></div>}
                  accessLabel={viewers === 1
                    ? '1 member has access'
                    : viewers > 1
                    ? `${viewers} members have access`
                    : 'No members have access'}
                  showStatusLabels={true}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManageAccess;
